import { EventPostRequest } from "../../clients/slashid-api/models/EventPostRequest"
import { SlashIDConfig } from "../../domain/options"
import { AnalyticsTransport } from "./analytics.types"

type Fetch = (input: URL | RequestInfo, init?: RequestInit | undefined) => Promise<Response>

const BROWSERS_WITHOUT_KEEPALIVE_SUPPORT = /firefox|fxios|deno/i

/**
 * Creates a fetch-based transport for sending analytics events.
 * Can be run in a browser or in a Node.js environment.
 * @param fetch - The fetch function to use for making HTTP requests.
 * @param config - The configuration object for the SlashID SDK.
 * @returns An object with a `sendEvent` method for sending analytics events.
 */
export const createFetchTransport = (fetch: Fetch, config: SlashIDConfig): AnalyticsTransport => {
    return {
        sendEvent: async (payload: EventPostRequest) => {
            try {
                await fetch(`${config.baseURL}/actions/sdk`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                })
            } catch (error) {
                console.error("@slashid/slashid-sdk: failed to send event", { error, event: payload })
            }
        },
    }
}

/**
 * Creates a beacon transport for sending analytics events. Can only be run in a browser.
 * @param config - The SlashID configuration.
 * @returns The analytics transport object.
 */
export const createBeaconTransport = (config: SlashIDConfig): AnalyticsTransport => {
    return {
        sendEvent: async (payload: EventPostRequest) => {
            const blob = new Blob([JSON.stringify(payload)], { type: "application/json" })
            navigator.sendBeacon(`${config.baseURL}/actions/sdk`, blob)
        },
    }
}

/**
 * Creates a fetch-based transport for sending analytics events, with support for keepalive.
 * This only runs in a browser environment.
 * @param fetch
 * @param config
 * @returns The analytics transport object.
 */
export const createFetchKeepAliveTransport = (fetch: Fetch, config: SlashIDConfig): AnalyticsTransport => {
    return {
        sendEvent: async (payload: EventPostRequest) => {
            const noKeepAliveSupport = BROWSERS_WITHOUT_KEEPALIVE_SUPPORT.test(navigator.userAgent)

            try {
                await fetch(`${config.baseURL}/actions/sdk`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                    keepalive: !noKeepAliveSupport,
                })
            } catch (error) {
                console.error("@slashid/slashid-sdk: failed to send event", { error, event: payload })
            }
        },
    }
}
